import React, { useEffect, useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import { Form, Input, Select, FormProps, message } from 'antd';
import styled from 'styled-components';
import { useCustomDimensionStore } from '../store';
import {
  useCreateCustomDimension,
  useCustomDimensionList,
  useDeleteCustomDimension,
  useUpdateCustomDimension,
} from '@src/emp-3.0/api/queryHooks';
import CustomDimensionRuleBuilder from '@src/emp-3.0/components/custom-dimension/custom-dimension-panel/components/RuleBuilder';
import {
  trimCustomDimensionPayloadMappedEventNames,
  validateCustomDimensionPayload,
} from '@src/emp-3.0/components/custom-dimension/utils';
import { validateDimensionName } from '@src/emp-3.0/domain/dimension';
import { ICreateCustomDimensionPayload, IUpdateCustomDimensionPayload } from '@src/emp-3.0/types';
import { trackClickWorkflowSave, trackDimensionOperation } from '@src/sensor';

import { categoryTypeOptions } from '@src/emp-3.0/components/custom-dimension/custom-dimension-panel/constant';
import {
  CustomDimensionDescription,
  MismatchNotification,
} from '@src/emp-3.0/components/custom-dimension/custom-dimension-panel/styled';
import CustomFormItem from '@src/emp-3.0/components/Metrics/IndividualMetric/MetricBuilder/CustomFormItem';
import { validateMetricName } from 'emp-3.0/components/Metrics/IndividualMetric/MetricDetail';
import { ISaveCallback } from '@src/pages/workflow/types';
import { useWorkflowStore } from '@src/pages/workflow/store';
import { EFormMode } from '@src/emp-3.0/types/common';
import { ALL_APPLICABLE_MAPPED_EVENTS_TYPE } from '@src/emp-3.0/constants/dimensions';

import styles from './index.less';
import useFieldRequiredStatus from '@src/pages/workflow/hooks/useFieldRequiredStatus';
import { StyledFormError } from '@src/pages/workflow/components/BuilderWrapper/styled';
import { useNavigate } from 'react-router-dom';

export const GroupTitle = styled.div`
  border-bottom: 1px solid rgba(0, 0, 0, 0.15);
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  padding: 8px 0;
`;

export const handleSuccess = (operation: string) => {
  trackDimensionOperation({ type: operation, status: 'success' });
  message.success(`Dimension ${operation === 'edit' ? 'Updated' : 'Created'} Successfully!`);
};

export const handleError = (operation: string) => (error: any) => {
  trackDimensionOperation({ type: operation, status: 'fail' });
};
interface ICustomDimensionProps {
  formProps: FormProps;
  onValuesChange?: (changedValues: any, allValues: any) => void;
  setServerSideError?: (v) => void;
}
// looks like this component is not being used.
const CustomDimension = forwardRef<any, ICustomDimensionProps>(
  ({ formProps, onValuesChange, setServerSideError }, ref) => {
    const { form } = formProps;
    const navigate = useNavigate();
    const { status, enableRequired } = useFieldRequiredStatus(['display_name']);
    const [validation, setValidation] = useState<{ failed: boolean; field: string }>({
      failed: false,
      field: '',
    });

    const { data: customDimensionList } = useCustomDimensionList();
    const { mutateAsync: createCustomDimension, isLoading: isCreateLoading } =
      useCreateCustomDimension();
    const { mutateAsync: updateCustomDimension, isLoading: isUpdateLoading } =
      useUpdateCustomDimension();
    const { mutate: deleteCustomDimension, isLoading: isDeleteLoading } =
      useDeleteCustomDimension();
    const { setFormMode } = useWorkflowStore();
    const {
      selectEventType,
      setSelectEventType,
      customDimensionPayload,
      setCustomDimensionPayload,
      setLoadingStates,
    } = useCustomDimensionStore((state) => state);

    const [notificationText, setNotificationText] = useState<string>('');

    const isUpdateDimension = 'custom_dimension_id' in customDimensionPayload;
    const isTraceId = useMemo(() => {
      if (isUpdateDimension) {
        const findDimension = (customDimensionList ?? []).find(
          (dimension) => dimension.id === customDimensionPayload.custom_dimension_id,
        );
        return (
          findDimension &&
          'dimension_identifier' in findDimension &&
          findDimension?.dimension_identifier === 'trace_id'
        );
      } else {
        return false;
      }
    }, [customDimensionPayload, customDimensionList, isUpdateDimension]);

    useEffect(() => {
      setLoadingStates({
        isCreateLoading,
        isUpdateLoading,
        isDeleteLoading,
      });
    }, [isCreateLoading, isDeleteLoading, isUpdateLoading, setLoadingStates]);

    useEffect(() => {
      const { failed, message } = validateDimensionName(customDimensionPayload.display_name);
      if (failed) {
        setNotificationText(message);
      } else {
        setNotificationText('');
      }
    }, [customDimensionPayload.display_name]);

    // When click on' New Custom Dimension' kebab to enter,
    // the default attribute_type may be inaccurate and needs to be obtained from the interface.
    useEffect(() => {
      form?.setFieldsValue({ category: customDimensionPayload.category });
    }, [customDimensionPayload.category]);

    const handleClickSave = async (cb?: ISaveCallback, innercb?: () => void) => {
      const isAllEvent = selectEventType === ALL_APPLICABLE_MAPPED_EVENTS_TYPE;
      const bypassFields = isAllEvent ? ['mapped_event_names'] : [];
      const formValues = form?.getFieldsValue();
      const customDimensionPayloadAssignForm = { ...customDimensionPayload, ...formValues };

      delete customDimensionPayloadAssignForm.event_type;
      const { failed, field } = validateCustomDimensionPayload(customDimensionPayloadAssignForm, [
        ...bypassFields,
        'display_name',
      ]);
      if (failed) {
        setValidation({ failed, field });
        return;
      }
      const payload: ICreateCustomDimensionPayload | IUpdateCustomDimensionPayload = isAllEvent
        ? { ...customDimensionPayloadAssignForm, mapped_event_names: [] }
        : trimCustomDimensionPayloadMappedEventNames(customDimensionPayloadAssignForm);
      if ('custom_dimension_id' in payload) {
        await updateCustomDimension(
          {
            custom_dimension_id: (payload as IUpdateCustomDimensionPayload).custom_dimension_id,
            queryBody: payload as IUpdateCustomDimensionPayload,
          },
          {
            onSuccess: () => {
              handleSuccess('edit');
              innercb?.();
              cb?.onSuccess?.();
              if (innercb) {
              } else {
                trackClickWorkflowSave({
                  type: 'dimension',
                  operation: 'edit',
                  id: payload.custom_dimension_id,
                });
              }
            },
            onError: (err: any) => {
              handleError('edit')(err);
              const errorMsg =
                err?.response?.data?.msg || err?.response?.data?.message || 'unknown error';
              if (setServerSideError) {
                setServerSideError(errorMsg);
              } else {
                message.error(errorMsg);
              }
              cb?.onError?.();
              if (innercb) {
              } else {
                trackClickWorkflowSave({
                  type: 'dimension',
                  operation: 'edit',
                  id: payload.custom_dimension_id,
                  error: errorMsg,
                });
              }
            },
            onSettled: () => {
              cb?.onSettled?.();
            },
          },
        );
      } else {
        await createCustomDimension(
          { queryBody: payload },
          {
            onSuccess: (data) => {
              handleSuccess('create');
              setFormMode(EFormMode.EDIT);

              const url = location.pathname.replace(/(\d+|new)/, data?.data.id);
              navigate(url, { replace: true });
              innercb?.();
              cb?.onSuccess?.();
              if (innercb) {
              } else {
                trackClickWorkflowSave({
                  type: 'dimension',
                  operation: 'create',
                  id: data?.data.id,
                });
              }
            },
            onError: (err: any) => {
              handleError('create')(err);
              const errorMsg =
                err?.response?.data?.msg || err?.response?.data?.message || 'unknown error';
              if (setServerSideError) {
                setServerSideError(errorMsg);
              } else {
                message.error(errorMsg);
              }
              cb?.onError?.();
              if (innercb) {
              } else {
                trackClickWorkflowSave({ type: 'dimension', operation: 'create', error: errorMsg });
              }
            },
            onSettled: () => {
              cb?.onSettled?.();
            },
          },
        );
      }
    };
    useImperativeHandle(ref, () => ({
      handleSave(cb?: ISaveCallback, innercb?: () => void) {
        handleClickSave(cb, innercb);
      },
    }));
    const handleChange = (value: { [key: string]: string | string[] | number }) => {
      setCustomDimensionPayload(value);
      form?.setFieldsValue(value);

      if (validation.field === 'mapped_event_names') {
        setValidation({ failed: false, field: '' });
      }

      if ('attribute_name' in value && validation.field === 'attribute_name') {
        setValidation({ failed: false, field: '' });
      }
      setServerSideError?.('');
    };

    return (
      <div style={{ margin: '24px' }} ref={ref}>
        <Form form={form} {...formProps} onValuesChange={onValuesChange}>
          <CustomFormItem
            name="display_name"
            label="Dimension Name"
            required
            validateStatus={status}
            rules={[
              {
                validator: (rule, value) =>
                  validateMetricName(rule, value, 'dimension', enableRequired),
              },
              {
                max: 256,
                message: 'Exceeds the maximum character limit(256)',
                type: 'string',
              },
            ]}
            labelStyle={{ minWidth: 140, textAlign: 'left' }}
          >
            <Input placeholder="Please input..." className={styles.removeAutofillBackground} />
          </CustomFormItem>
          <CustomFormItem
            name="category"
            label="Dimension Category"
            required
            labelStyle={{ minWidth: 140, textAlign: 'left' }}
          >
            <Select
              disabled
              style={{ width: '100%' }}
              options={categoryTypeOptions}
              placeholder="Category auto-filled with tag key selection."
            />
          </CustomFormItem>
          {notificationText && (
            <MismatchNotification>
              <span>{notificationText}</span>
            </MismatchNotification>
          )}
          {/* <GroupTitle>Mapping Rules</GroupTitle> */}
          <CustomDimensionDescription>
            Select a Tag Key and specify the Events to activate.
          </CustomDimensionDescription>
          {validation.failed && (
            <StyledFormError style={{ marginBottom: 5 }}>
              * Required fields in red are missing.
            </StyledFormError>
          )}
          <CustomDimensionRuleBuilder
            errorField={validation.field}
            eventTag={customDimensionPayload.attribute_name}
            eventNames={customDimensionPayload.mapped_event_names}
            attributeType={customDimensionPayload.attribute_type}
            selectEventType={selectEventType}
            onEventTagChange={(value) => handleChange({ attribute_name: value })}
            onEventNamesChange={(value) => handleChange({ mapped_event_names: value })}
            onAttributeTypeChange={(value) => handleChange({ attribute_type: value })}
            onCategoryChange={(value) => handleChange({ category: value })}
            onSelectEventTypeChange={(value) => {
              handleChange({ event_type: value });
              setSelectEventType(value);
            }}
          />
        </Form>
      </div>
    );
  },
);
CustomDimension.displayName = 'CustomDimension';
export default CustomDimension;
